<template>
  <div>
    <div class="card-header">
      <div class="d-flex justify-content-between align-items-center">
        <div class="custom-search">
          <b-form-group>
            <div class="d-flex align-items-center">
              <span class="text-nowrap"> {{ $t("rowsPerPage") }} </span>
              <b-form-select
                v-model="pageLength"
                :options="['25', '50', '100']"
                class="ml-1"
                @input="(value) => onPerPageChange({ pageLength: value })"
              />
            </div>
          </b-form-group>
        </div>
      </div>
    </div>
    <!--==================================-->
    <div class="dextop-table-view">
      <div class="table-responsive api-keys-table">
        <vue-good-table
          styleClass="vgt-table striped"
          :columns="columns"
          :fixed-header="false"
          :rows="rows"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm,
            trigger: 'enter',
          }"
          :pagination-options="{
            enabled: true,
            perPage: pageLength,
          }"
          mode="remote"
          @on-page-change="onPageChange"
          @on-sort-change="onSortChange"
          @on-per-page-change="onPerPageChange"
        >
          <template slot="table-row" slot-scope="props">
            <!-- Column: Name -->
            <span v-if="props.column.field === 'createdAt'">
              <p class="">
                {{ $dateFormatter(props.row.createdAt, $i18n.locale) }}
              </p>
            </span>
            <span
              class="d-flex align-items-center justify-content-center gap-2"
              v-else-if="props.column.field === 'action'"
            >
              <div
                v-if="props.row.usedCredits.length > 0"
                class="cursor-pointer"
                @click="openModal(props.row)"
              >
                <feather-icon size="16" icon="EyeIcon" />
              </div>
            </span>
            <span v-else-if="props.column.field === 'costs'">
              <p class="">
                {{
                  $formatter(props.row.costs, $i18n.locale, "EUR", false, 2, 2)
                }}
              </p>
            </span>
            <!-- Column: Common -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
          <!-- pagination -->
          <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-center flex-wrap">
              <div>
                <b-pagination
                  :value="1"
                  :total-rows="totalRecords"
                  :per-page="pageLength"
                  first-number
                  last-number
                  align="right"
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value) => onPageChange({ currentPage: value })"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>
    <div class="reponsive-view-cards">
      <div class="row">
        <div class="col-md-6" v-for="row in rows" :key="row.id">
          <div class="card mb-1">
            <div
              class="card-header d-flex align-items-center justify-content-between"
            >
              <h5 class="card-title">
                {{ $dateFormatter(row.createdAt, $i18n.locale) }}
              </h5>
              <div class="d-flex gap-2">
                <div
                  v-if="row.usedCredits && row.usedCredits.length > 0"
                  class="cursor-pointer"
                  @click="openModal(row)"
                >
                  <feather-icon size="18" icon="EyeIcon" />
                </div>
              </div>
            </div>
            <div class="card-body pt-0">
              <ul class="card-list">
                <li>
                  <div class="left">
                    <strong>{{ $t("type") }}: </strong>
                  </div>
                  <div class="right">
                    <span>{{ row.type }}</span>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("amountCredits") }}: </strong>
                  </div>
                  <div class="right">
                    <span>{{ row.amountCoins }}</span>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("counterParty") }}: </strong>
                  </div>
                  <div class="right">
                    <span>{{ row.counterParty }}</span>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("currentCredits") }}: </strong>
                  </div>
                  <div class="right">
                    <span>{{ row.currentCoins }}</span>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("costs") }}: </strong>
                  </div>
                  <div class="right">
                    <span>
                      {{
                        $formatter(
                          row.costs,
                          $i18n.locale,
                          "EUR",
                          false,
                          2,
                          2
                        )
                      }}
                    </span>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("transactionId") }}: </strong>
                  </div>
                  <div class="right">
                    <span>{{ row.transactionId }}</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-center">
        <b-pagination
          :value="1"
          :total-rows="totalRecords"
          :per-page="pageLength"
          first-number
          last-number
          align="right"
          prev-class="prev-item"
          next-class="next-item"
          class="mt-1 mb-0"
          @input="(value) => onPageChange({ currentPage: value })"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </div>
    </div>
    <!--==================================-->
    <b-modal
      id="edit-tag-modal"
      v-model="showModal"
      :title="$t('usedCredits')"
      centered
      size="lg"
      hide-footer
    >
      <div class="table-responsive mt-1">
        <table class="vgt-table striped">
          <thead>
            <tr>
              <th>{{ $t("purchaseDate") }}</th>
              <th>{{ $t("creditType") }}</th>
              <th>{{ $t("amountCredits") }}</th>
              <th>{{ $t("pricePerCredit") }}</th>
              <th>{{ $t("totalCosts") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(credit, index) in currentCredits" :key="index">
              <td>{{ $dateFormatter(credit.createdAt, $i18n.locale) }}</td>
              <td>{{ credit.paymentType }}</td>
              <td>{{ credit.creditsUsed }}</td>
              <!-- <td>{{ products.tax }}%</td> -->
              <td>
                {{
                  $formatter(
                    credit.costPerCredit,
                    $i18n.locale,
                    "EUR",
                    false,
                    2,
                    2
                  )
                }}
              </td>
              <td>
                {{
                  $formatter(credit.totalCost, $i18n.locale, "EUR", false, 2, 2)
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="d-flex align-items-center justify-content-end mt-2">
        <button @click="showModal = false" class="btn btn-secondary mr-1">
          {{ $t("close") }}
        </button>
        <button @click="goToInvoice()" class="btn btn-primary">
          {{ $t("goToInvoice") }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { debounce } from "@/utils/debounce";

export default {
  computed: {
    columns() {
      return [
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("date"),
          field: "createdAt",
        },
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("type"),
          field: "type",
        },
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("amountCredits"),
          field: "amountCoins",
        },
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("counterParty"),
          field: "counterParty",
          sortable: false,
        },
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("currentCredits"),
          field: "currentCoins",
        },
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("costs"),
          field: "costs",
        },
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("transactionId"),
          field: "transactionId",
        },
        {
          tdClass: "vgt-left-align",
          thClass: "vgt-left-align",
          label: this.$t("action"),
          field: "action",
          sortable: false,
        },
      ];
    },
  },
  data() {
    return {
      file: null,
      totalRecords: 0,
      pageLength: 25,
      page: 1,
      sortBy: "createdAt",
      sortOrder: "desc",
      rows: [],
      searchTerm: "",
      showModal: false,
      selectedInvoiceId: "",
      currentCredits: [],
    };
  },

  async created() {
    this.debouncedFetch = debounce(async (newValue, oldValue) => {
      try {
        await this.loadItems();
      } catch (e) {
        console.error(e);
      }
    }, 300);
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this, newProps);
    },
    openModal(props) {
      this.selectedInvoiceId = props.invoiceId;
      this.currentCredits = props.usedCredits;
      this.showModal = true;
    },
    goToInvoice() {
      window.open(`/invoices/${this.selectedInvoiceId}/show`, "_blank");
    },
    onPageChange(params) {
      this.page = params.currentPage;
      this.loadItems();
    },

    onPerPageChange(params) {
      this.updateParams({ pageLength: params.pageLength });
      this.loadItems();
    },

    onSortChange(params) {
      this.sortOrder = params[0].type;
      if (params[0].type == "none") this.sortOrder = "asc";
      this.sortBy = params[0].field;
      this.loadItems();
    },
    // load items is what brings back the rows from server
    async loadItems() {
      try {
        this.$store.commit("showLoader", true);
        let response = await this.$store.dispatch("customers/getBalances", {
          id: this.$route.params.id,
          data: {
            page: this.page,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            perPage: this.pageLength,
          },
        });
        this.rows = response?.data?.data;
        this.totalRecords = response?.data?.total;
      } catch (e) {
        console.error("api error ___", e);
      } finally {
        this.$store.commit("showLoader", false);
      }
    },
  },
};
</script>

<style>
#nprogress {
  position: relative;
  z-index: 9999999;
}

.white-color {
  color: white !important;
}

.vgt-responsive {
  overflow-x: visible !important;
}
</style>
